<script setup lang="ts">
import CurrencyText from '@uiKit/components/CurrencyText.vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'

export interface TransactionFact {
  label: string
  value: string
  /** Semantic colour for the value (status rows). Defaults to body text. */
  color?: string
}

/**
 * `row` puts the label left and value right — the transaction detail panels.
 * `stacked` puts the label above the value, which is what the deposit receipt
 * needs so a long bank reference can wrap across the card's full width.
 */
export type TransactionFactLayout = 'row' | 'stacked'

// One titled card of label → value rows, divided by hairlines.
interface Props {
  title: string
  facts: TransactionFact[]
  layout?: TransactionFactLayout
}

withDefaults(defineProps<Props>(), {
  layout: 'row',
})
</script>

<template>
  <Box direction="col" gap="4" is="section" class="tfc">
    <Text type="body" weight="bold" color="muted" is="h2">{{ title }}</Text>

    <Box direction="col" class="tfc__rows">
      <Box
        v-for="fact in facts"
        :key="fact.label"
        :direction="layout === 'stacked' ? 'col' : undefined"
        :align="layout === 'stacked' ? 'start' : 'center'"
        :justify="layout === 'stacked' ? undefined : 'between'"
        :gap="layout === 'stacked' ? '1' : '4'"
        class="tfc__row"
      >
        <Text type="heading" weight="regular" color="muted" is="span">{{ fact.label }}</Text>
        <Text
          type="heading"
          weight="medium"
          :align="layout === 'stacked' ? 'left' : 'right'"
          is="span"
          :color="fact.color ?? 'default'"
          class="tfc__value"
        >
          <CurrencyText :text="fact.value" />
        </Text>
      </Box>
    </Box>
  </Box>
</template>

<style scoped>
.tfc {
  flex: 1;
  min-width: 0;
  background: var(--p-surface-0);
  border: 1px solid var(--p-content-border-color);
  border-radius: 16px;
  padding: 1.375rem 1.5rem;
  box-sizing: border-box;
}

.tfc__rows {
  width: 100%;
}

.tfc__row {
  padding: 0.8125rem 0;
}

.tfc__row:not(:last-child) {
  border-bottom: 1px solid var(--p-content-border-color);
}

.tfc__value {
  min-width: 0;
  word-break: break-word;
}
</style>
